import { ChangeDetectionStrategy, Component, ErrorHandler, Injector, OnDestroy, OnInit, inject, signal } from '@angular/core'

import { Asiakas } from '../../../_jaettu-lemonator/model/asiakas'

import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from '../../../_angular/service/asiakas/asiakas.service'

import { FieldTree, form, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { BankConsentLog, BankCustomerConsent, BankDialogType } from '@lemon/core-firebase/model/banks'
import { SahkoisenTiliotteenAlatyyppi } from '@lemon/core-firebase/model/tiliote'
import { BankConsentUriService } from '@lemon/core-firebase/service/bank-consent-uri.service'
import { BicService } from '@lemon/core/service/bic.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import {
  EnableBankingGetSessionAndAccountRequest,
  EnableBankingGetSessionAndAccountResponse,
  EnableBankingHaeIbanitLemonatorRequest,
  EnableBankingHaeIbanitLemonatorResponse,
  EnableBankingSyncAccountTransactionsJobData,
  EnableBankingTransaction,
  GetRawEnableBankingTransactionsQuery,
  GetRawEnableBankingTransactionsResponse,
} from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/enable-banking-backend'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of as observableof, Subject } from 'rxjs'
import { map, switchMap, takeUntil, tap } from 'rxjs/operators'

interface ViewableTransaction extends EnableBankingTransaction {
  showDetails: boolean
}

interface RawTransactionsState {
  showTable: boolean
  loading: boolean
  transactions: ViewableTransaction[]
}

interface AccountSelectingInfo {
  iban: string
  bic: string
  bank: string
}

interface TilitapahtumienHakuLomake {
  iban: AccountSelectingInfo | null
  start: Date | null
  end: Date | null
}

@Component({
  selector: '[app-enable-banking-tilitapahtumat]',
  templateUrl: './enable-banking-tilitapahtumat.component.html',
  styleUrls: ['./enable-banking-tilitapahtumat.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EnableBankingTilitapahtumatComponent implements OnInit, OnDestroy {
  asiakas: Asiakas = null

  consentHtmlObservable: Observable<string>
  userLevelConsentsObservable: Observable<string>
  asiakkaanIbanitObservable: Observable<AccountSelectingInfo[]>
  private _historianMaaraSubject: BehaviorSubject<number> = new BehaviorSubject(50)
  historianMaaraObservable = this._historianMaaraSubject.asObservable()
  logsObservable: Observable<BankConsentLog[]>
  lomake: FieldTree<TilitapahtumienHakuLomake>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  rawTransactionsStateSubject = new BehaviorSubject<RawTransactionsState>({ showTable: false, loading: false, transactions: [] })
  readonly session = signal<EnableBankingGetSessionAndAccountResponse['session']>(null)
  readonly account = signal<EnableBankingGetSessionAndAccountResponse['accountInfo']>(null)
  readonly balance = signal<EnableBankingGetSessionAndAccountResponse['balance']>(null)

  accountAndSessionLoadingSubject = new BehaviorSubject<boolean>(false)

  constructor(
    private _asiakasService: AsiakasService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dateService: DateService,
    private _formValidationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _bicService: BicService,
    private _snackbar: MatSnackBar,
    private _bankConsentUriService: BankConsentUriService,
    private _timestampService: TimestampProviderBase,
  ) {}

  ngOnInit() {
    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(result => {
      this.asiakas = result
    })

    const endDate = new Date()
    const startDate = this._dateService.kuukaudenEnsimmainen(this._dateService.lisaaKuukausia(endDate, -2))

    this.lomake = form(
      signal<TilitapahtumienHakuLomake>({ iban: null, start: startDate, end: endDate }),
      polku => {
        required(polku.iban)
        required(polku.start)
        required(polku.end)
      },
      { injector: this._injector },
    )
    const ibanKentta = this.lomake.iban

    const bankCustomerConsentObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas?.avain) {
          return observableof<BankCustomerConsent>(null)
        }
        const uri = this._bankConsentUriService.getBankConsentUri(asiakas.avain)

        return this._firebaseLemonaid.firestoreDoc<BankCustomerConsent>(uri).listen()
      }),
    )

    this.consentHtmlObservable = bankCustomerConsentObservable.pipe(
      map(consent => {
        if (!consent) {
          return '<p>Käyttäjillä ei ole pankkiyhteysroolia</p>'
        }
        if (!consent.dialogsToShow?.length && !consent.forceDialogToShow?.length) {
          return '<p>Kaikki maksutavoissa olevat sähköiset tiliotteet toimivat.</p>'
        }
        const getDialogName = (dialogToShow: BankDialogType): string => {
          switch (dialogToShow) {
            case BankDialogType.GENERAL_WO_HOLVI:
              return 'Yleinen pankkiyhteys (ilman Holvia)'
            case BankDialogType.GENERAL:
              return 'Yleinen pankkiyhteys'
            case BankDialogType.PSD2_RENEW:
              return 'Aiia / Mastercard pankkiyhteys'
            case BankDialogType.HOLVI_RENEW:
              return 'Holvi pankkiyhteys'
            case BankDialogType.ENABLE_BANKING:
              return 'Enable Banking pankkiyhteys'
            default:
              return 'Tuntematon dialogi'
          }
        }
        let output = '<p>Pankkiyhteysikkuna on päällä:<br/><br/>'
        if (consent.forceDialogToShow) {
          output += getDialogName(consent.forceDialogToShow) + ' (pakotetusti päällä)'
        }
        if (consent.dialogsToShow.length) {
          for (const d of consent.dialogsToShow) {
            output +=
              getDialogName(d?.dialogToShow) +
              '<br/>' +
              'Pankin tunnus ' +
              (d.providerMetadata?.aiiaMetadata?.providerId ?? d.providerId ?? 'Tuntematon') +
              ', vanhentunut ' +
              this._dateService.muotoile(this._dateService.numberTimestampToDate(d.connectionLost), 'dd.MM.yyyy HH:mm:ss') +
              '<br/>' +
              (d.ibansMissing ? d.ibansMissing?.join(', ') : '') +
              '</p>'
          }
        }
        return output
      }),
    )

    this.asiakkaanIbanitObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return observableof<AccountSelectingInfo[]>(null as AccountSelectingInfo[])
        }
        const ibanRequestData: EnableBankingHaeIbanitLemonatorRequest = {
          asiakasAvain: asiakas.avain,
        }
        return this._firebaseLemonaid
          .functionsCall<EnableBankingHaeIbanitLemonatorRequest, EnableBankingHaeIbanitLemonatorResponse>('enableBankingGetCustomerIbansLemonator', ibanRequestData)
          .then(data => {
            if (!data || data.e || !data.ibanit) {
              return []
            }
            return data.ibanit
              .filter(iban => !!iban)
              .map(iban => {
                const bic = this._bicService.annaBicKoodi(iban) || 'BIC ei tiedossa'
                const bank = this._bicService.annaPankinNimi(iban) || 'Pankin nimi ei tiedossa'
                const val: AccountSelectingInfo = { iban: iban, bic: bic, bank: bank }
                return val
              })
          })
      }),
      tap(ibanit => {
        if (!ibanKentta().value() && ibanit?.length > 0) {
          asetaArvo(ibanKentta, ibanit[0])
        }
      }),
    )

    this.logsObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.historianMaaraObservable]).pipe(
      switchMap(([asiakas, historianMaara]) => {
        if (!asiakas) {
          return observableof<BankConsentLog[]>([])
        }
        const uri = this._bankConsentUriService.getBankConsentLogsCollection(asiakas.avain)
        return this._firebaseLemonaid.firestoreCollection<BankConsentLog>(uri).orderBy('changedTime', 'desc').limit(Number(historianMaara)).listen()
      }),
    )
  }

  async getRawEnableBankingTransactions() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.rawTransactionsStateSubject.value.loading = true
    this.rawTransactionsStateSubject.value.showTable = true
    this.rawTransactionsStateSubject.next(this.rawTransactionsStateSubject.value)

    try {
      const asiakasAvain = this.asiakas.avain

      if (!asiakasAvain) {
        throw new Error('No asiakasAvain')
      }

      const iban = this.lomake.iban().value()
      const startDate = this._dateService.dateToNumber(this.lomake.start().value())
      const endDate = this._dateService.dateToNumber(this.lomake.end().value())

      const transactionsPayload: GetRawEnableBankingTransactionsQuery = {
        asiakasAvain: asiakasAvain,
        iban: iban.iban,
        start: startDate,
        end: endDate,
      }

      const transactionsResponse = await this._firebaseLemonaid.functionsCall<GetRawEnableBankingTransactionsQuery, GetRawEnableBankingTransactionsResponse>(
        'enableBankingGetRawTransactions',
        transactionsPayload,
        { timeout: 540 * 1000 },
      )

      if (!transactionsResponse || transactionsResponse.e) {
        throw new Error('Failed to get transactions: ' + (transactionsResponse?.e || 'no response'))
      }

      this.rawTransactionsStateSubject.value.transactions = transactionsResponse.transactions as ViewableTransaction[]
      this.rawTransactionsStateSubject.value.loading = false
      this.rawTransactionsStateSubject.next(this.rawTransactionsStateSubject.value)
    } catch (err) {
      this._errorHandler.handleError(err)
      this.rawTransactionsStateSubject.value.loading = false
      this.rawTransactionsStateSubject.next(this.rawTransactionsStateSubject.value)
    }
  }

  async getSessionAndAccountDetails() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.accountAndSessionLoadingSubject.next(true)

    try {
      const asiakasAvain = this.asiakas.avain

      if (!asiakasAvain) {
        throw new Error('No asiakasAvain')
      }

      const iban = this.lomake.iban().value()

      const sessionAndAccountPayload: EnableBankingGetSessionAndAccountRequest = {
        asiakasAvain: asiakasAvain,
        iban: iban.iban,
      }

      const resp = await this._firebaseLemonaid.functionsCall<EnableBankingGetSessionAndAccountRequest, EnableBankingGetSessionAndAccountResponse>(
        'enableBankingGetSessionAndAccountDetails',
        sessionAndAccountPayload,
        { timeout: 540 * 1000 },
      )

      if (!resp || resp.e) {
        throw new Error('Failed to get session and account details: ' + (resp?.e || 'no response'))
      }

      this.session.set(resp.session)
      this.account.set(resp.accountInfo)
      this.balance.set(resp.balance)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.accountAndSessionLoadingSubject.next(false)
    }
  }

  toggleShowDetails(transaction: ViewableTransaction) {
    if (transaction.showDetails) {
      delete transaction.showDetails
    } else {
      transaction.showDetails = true
    }
  }

  async startEnableBankingFetch() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    try {
      const asiakasAvain = this.asiakas.avain

      if (!asiakasAvain) {
        throw new Error('No asiakasAvain')
      }

      const iban = this.lomake.iban().value()

      const maksutavat = await firstValueFrom(this._asiakasService.nykyisenAsiakkaanMaksutavatObservable)
      const valitunTilinMaksutapa = maksutavat.find(maksutapa => maksutapa.sahkoisenTiliotteenTunniste === iban.iban)
      if (!valitunTilinMaksutapa || valitunTilinMaksutapa.sahkoisenTiliotteenAlatyyppi !== SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING) {
        this._snackbar.open(`Tilin tapahtumia ei voida tuoda kirjanpitoon Enable Banking-kanavasta, maksutapa on konfiguroitu tyypiksi ${valitunTilinMaksutapa?.sahkoisenTiliotteenAlatyyppi}`, 'OK')
        return
      }

      const startDateNum = this._dateService.dateToNumber(this.lomake.start().value())
      const endDateNum = this._dateService.dateToNumber(this.lomake.end().value())
      const jobData: EnableBankingSyncAccountTransactionsJobData = {
        asiakasAvain: asiakasAvain,
        delayedTimes: 0,
        delayedReasons: [],
        iban: iban.iban,
        start: startDateNum,
        end: endDateNum,
        tryAgainEarliest: this._timestampService.dateToTimestamp(new Date(1980, 1, 5)),
      }

      await this._firebaseLemonaid.firestoreSetData('tyojono/' + asiakasAvain + '/enable-banking-start-transactions-fetch/' + this._firebaseLemonaid.firestoreCreateId(), jobData)

      this._snackbar.open(
        'Tilin ' +
          iban.iban +
          ' tilitapahtumien synkronointi kirjanpitoon on käynnistetty aikavälillä ' +
          this._dateService.muotoileNumberPaiva(startDateNum, 'fi') +
          ' - ' +
          this._dateService.muotoileNumberPaiva(endDateNum, 'fi') +
          '.',
        'OK',
      )
    } catch (err) {
      this._errorHandler.handleError(new Error('EnableBanking transaction manual fetch start failed: ' + (err?.message ?? 'unknown error')))
    }
  }

  historianMaaraChanged(limit: string) {
    this._historianMaaraSubject.next(+limit)
  }

  // private _ibanValidator: ValidatorFn = (c: AbstractControl<AccountSelectingInfo>): ValidationErrors | null => {
  //   // console.log(c.value, this.ibanService.isValidIban(c.value))
  //   if (c && c.value && c.value.bank && !this._ibanService.isValidIban(c.value.bank)) {
  //     return { 'iban': true }
  //   }
  //   return null
  // }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
